import { useEffect, useRef, useState } from 'react';
import { discoveryApiRef, fetchApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

// Aligned with the backend query cache TTL (15 min) — polling more frequently
// returns the same cached response and generates unnecessary backend log volume.
export const POLL_INTERVAL_MS = 900_000;

export interface SloBreachTeam {
  name: string;
  critical: number;
  high: number;
  total: number;
}

export interface UseSecuritySloBreachesResult {
  teams: SloBreachTeam[];
  overflowCount: number;
  loaded: boolean;
}

export function useSecuritySloBreaches(): UseSecuritySloBreachesResult {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [teams, setTeams] = useState<SloBreachTeam[]>([]);
  const [overflowCount, setOverflowCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const mountedRef = useRef(true);
  const failedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    failedRef.current = false;
    setLoaded(false);

    const poll = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const groupsResponse = await catalogApi.getEntities({
          filter: { kind: 'Group', 'relations.hasMember': identity.userEntityRef },
          fields: ['metadata.name'],
        });

        if (groupsResponse.items.length === 0) {
          if (mountedRef.current) {
            setTeams([]);
            setOverflowCount(0);
            setLoaded(true);
          }
          return;
        }

        const groupNames = groupsResponse.items.map(g => g.metadata.name).filter(Boolean);
        if (groupNames.length === 0) {
          if (mountedRef.current) {
            setTeams([]);
            setOverflowCount(0);
            setLoaded(true);
          }
          return;
        }
        const slugs = groupNames.sort().slice(0, 100).join(',');

        const baseUrl = await discoveryApi.getBaseUrl('security-insights');
        const res = await fetchApi.fetch(
          `${baseUrl}/slo-breaches?teams=${encodeURIComponent(slugs)}`,
        );
        if (!res.ok) {
          throw new Error(`SLO breaches request failed with status ${res.status}`);
        }
        const body = await res.json();
        const components: SloBreachTeam[] = Array.isArray(body?.components)
          ? body.components
          : [];
        const overflow = typeof body?.overflowCount === 'number' ? body.overflowCount : 0;
        failedRef.current = false;
        if (mountedRef.current) {
          setTeams(components);
          setOverflowCount(overflow);
          setLoaded(true);
        }
      } catch (err) {
        if (!failedRef.current) {
          // eslint-disable-next-line no-console
          console.warn('useSecuritySloBreaches: fetch failed, will retry silently', err);
          failedRef.current = true;
          // On failure, teams and overflowCount retain their last-good-value so the
          // banner stays visible during transient network blips.
        }
      }
    };

    void poll();
    const interval = setInterval(() => void poll(), POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [discoveryApi, fetchApi, identityApi, catalogApi]);

  return { teams, overflowCount, loaded };
}
